import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { formatDateNZ } from "../lib/format";
import { claimWindowStatus, RETAILER_POLICIES } from "../lib/retailerPolicies";
import Header from "../components/Header";
import LaneNav from "../components/LaneNav";
import { toast } from "sonner";
import { ArrowLeft, ArrowsClockwise, EnvelopeSimple, Copy, Trash, ArrowDown, ArrowRight, Wrench, ArrowSquareOut, Receipt as ReceiptIcon, CheckCircle, Upload, PaperPlaneTilt, Warning, Gift } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine, CartesianGrid } from "recharts";
import ComparePrices from "../components/ComparePrices";
import RetailerPolicyNote from "../components/RetailerPolicyNote";
import { Link } from "react-router-dom";
import { outboundRetailerUrl, trackBuyNowClick } from "../lib/retailerLinks";

export default function PurchaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [email, setEmail] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [notes, setNotes] = useState("");
  // Auto-claim send state
  const [orderNumber, setOrderNumber] = useState("");
  const [sendingClaim, setSendingClaim] = useState(false);
  const [claimSent, setClaimSent] = useState(null); // { sent_to, sent_at, potential_refund }
  // Warranty claim state
  const [warrantyIssue, setWarrantyIssue] = useState("");
  const [warrantyTried, setWarrantyTried] = useState("");
  const [warrantyMonthsOverride, setWarrantyMonthsOverride] = useState("");
  const [warrantyEmail, setWarrantyEmail] = useState(null);
  const [warrantyBusy, setWarrantyBusy] = useState(false);
  // Linked receipt state
  const [attaching, setAttaching] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/purchases/${id}`);
      setP(data);
    } catch {
      toast.error("Purchase not found");
      navigate("/dashboard");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Load any saved warranty draft for this purchase so a page refresh doesn't lose work.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/purchases/${id}/warranty-claim-email/saved`);
        if (cancelled || !data?.draft) return;
        const d = data.draft;
        setWarrantyEmail({
          subject: d.subject,
          body: d.body,
          warranty_months: d.warranty_months,
          warranty_expires_at: d.warranty_expires_at,
          warranty_days_left: d.warranty_expires_at
            ? Math.ceil((new Date(d.warranty_expires_at) - new Date()) / (1000 * 60 * 60 * 24))
            : null,
          inside_manufacturer_window: d.inside_manufacturer_window,
        });
        if (d.issue_description) setWarrantyIssue(d.issue_description);
        if (d.contact_attempts) setWarrantyTried(d.contact_attempts);
        if (d.warranty_months) setWarrantyMonthsOverride(String(d.warranty_months));
      } catch (e) { /* no saved draft */ }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/purchases/${id}/refresh`);
      setP(data);
      toast.success("Prices refreshed");
    } catch (e) { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const removeItem = async () => {
    if (!window.confirm("Stop tracking this purchase?")) return;
    try {
      await api.delete(`/purchases/${id}`);
      toast.success("Removed from watchlist");
      navigate("/dashboard");
    } catch { toast.error("Delete failed"); }
  };

  const generateClaim = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/purchases/${id}/claim-email`, { extra_notes: notes || null });
      setEmail(data);
      setClaimSent(null); // new draft = new send opportunity
      toast.success("Price adjustment claim ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate email");
    } finally { setGenBusy(false); }
  };

  const sendClaimToRetailer = async () => {
    if (!email) return;
    if (!p.linked_vault_id) {
      toast.error("Attach the receipt first - retailers want proof of purchase before refunding the difference.");
      return;
    }
    const ok = window.confirm(
      `We'll send this email to ${p.retailer_name} on your behalf with the receipt attached. The retailer will see it's from "${(p.user_name || "you")}" via PriceGuard and any reply will go straight to your inbox.\n\nProceed?`
    );
    if (!ok) return;
    setSendingClaim(true);
    try {
      const { data } = await api.post(`/purchases/${id}/send-claim`, {
        subject: email.subject,
        body: email.body,
        order_number: orderNumber.trim() || null,
      });
      setClaimSent(data);
      toast.success(`Claim sent to ${data.sent_to}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send the claim. Try again, or copy the email and send from your own inbox.";
      toast.error(detail);
    } finally {
      setSendingClaim(false);
    }
  };

  const markSelfSubmitted = async () => {
    // For form-mode retailers (JB Hi-Fi). One-tap record that the user
    // pasted the draft into the retailer's contact form and hit submit
    // there. Backend enforces same drop+window gates as /send-claim.
    setSendingClaim(true);
    try {
      const { data } = await api.post(`/purchases/${id}/mark-claim-submitted`);
      setClaimSent({
        sent_to: data.retailer_name + " (form)",
        sent_at: data.submitted_at,
        reply_to: "your inbox (whichever you used on the form)",
        potential_refund: data.potential_refund,
        self_submitted: true,
      });
      toast.success(`Recorded — good luck with ${data.retailer_name}.`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't record submission.";
      toast.error(detail);
    } finally {
      setSendingClaim(false);
    }
  };

  const generateWarrantyClaim = async () => {
    if (warrantyIssue.trim().length < 10) {
      toast.error("Describe the fault in at least a sentence");
      return;
    }
    setWarrantyBusy(true);
    try {
      const payload = {
        issue_description: warrantyIssue.trim(),
        contact_attempts: warrantyTried.trim() || null,
        warranty_months_override: warrantyMonthsOverride ? Number(warrantyMonthsOverride) : null,
      };
      const { data } = await api.post(`/purchases/${id}/warranty-claim-email`, payload);
      setWarrantyEmail(data);
      toast.success("Warranty claim draft ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate warranty claim");
    } finally { setWarrantyBusy(false); }
  };

  const saveWarrantyDraft = async () => {
    if (!warrantyEmail) return;
    try {
      await api.post(`/purchases/${id}/warranty-claim-email/save`, {
        subject: warrantyEmail.subject,
        body: warrantyEmail.body,
        issue_description: warrantyIssue || null,
        contact_attempts: warrantyTried || null,
        warranty_months: warrantyEmail.warranty_months,
        warranty_expires_at: warrantyEmail.warranty_expires_at,
        inside_manufacturer_window: warrantyEmail.inside_manufacturer_window,
      });
      toast.success("Draft saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save draft");
    }
  };

  const clearWarrantyDraft = async () => {
    if (!window.confirm("Discard this warranty draft?")) return;
    try {
      await api.delete(`/purchases/${id}/warranty-claim-email/saved`);
      setWarrantyEmail(null);
      setWarrantyIssue("");
      setWarrantyTried("");
      setWarrantyMonthsOverride("");
      toast.success("Draft cleared");
    } catch (e) {
      toast.error("Could not clear draft");
    }
  };

  const attachReceipt = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Max 10 MB.");
      return;
    }
    setAttaching(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/purchases/${id}/attach-receipt`, form);
      toast.success("Receipt attached - it's now in your vault too");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not attach receipt");
    } finally {
      setAttaching(false);
    }
  };

  const detachReceipt = async () => {
    if (!window.confirm("Detach this receipt from the purchase? The file will be removed from your vault.")) return;
    try {
      await api.delete(`/purchases/${id}/receipt`);
      toast.success("Receipt detached - you can upload a fresh one now");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not detach receipt");
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (!p) return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 py-12" data-testid="purchase-loading">
        <div className="space-y-4">
          <div className="h-3 w-32 bg-[#e4e4e7] animate-pulse" />
          <div className="h-10 w-2/3 bg-[#e4e4e7] animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mt-10">
          <div className="bg-white p-6 h-28 animate-pulse" />
          <div className="bg-white p-6 h-28 animate-pulse" />
          <div className="bg-white p-6 h-28 animate-pulse" />
        </div>
        <div className="h-72 bg-[#f7f7f8] border border-[#e4e4e7] mt-10 animate-pulse" />
      </div>
    </div>
  );

  const diff = p.purchase_price - p.current_price;
  const isDrop = diff > 0;
  const pct = ((diff / p.purchase_price) * 100).toFixed(1);
  const expDate = new Date(p.tracking_expires_at);
  const daysLeft = Math.max(0, Math.ceil((expDate - new Date()) / (1000*60*60*24)));
  // Retailer-specific refund-claim window — the legally relevant deadline
  // (PB Tech 30, JB Hi-Fi 14, Harvey Norman 7). Distinct from our internal
  // 42-day tracking window: we keep checking the price for 6 weeks total,
  // but only the first N days count as "claim eligible" per retailer policy.
  const claim = claimWindowStatus(p.retailer_id, p.purchase_date);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <LaneNav active="purchases" />
        <button onClick={() => navigate("/dashboard")} className="text-sm font-semibold inline-flex items-center gap-1 text-[#52525b] hover:text-black mb-6" data-testid="detail-back">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
          <div>
            <div className="pg-overline">{p.retailer_name} · {p.category}</div>
            <h1 className="font-heading text-4xl font-black tracking-tighter mt-2">{p.product_name}</h1>
            <div className="font-mono-pg text-xs text-[#52525b] mt-2">
              Purchased {formatDateNZ(p.purchase_date)}
            </div>
            {/* Two distinct windows, each with their own role - but
                we never show both as separate chips because users read
                "Claim window closed · Watching daily: 14 days left" as
                a contradiction. Rules:
                 - If the retailer's claim window is OPEN: show the
                   green "claim window: Nd left" chip only. That's the
                   thing the user actually needs to act on.
                 - If the claim window is CLOSED: show one neutral
                   chip that says "Claim window closed (Retailer Nd)"
                   - we don't surface the background tracking ticker
                   because it doesn't change what the user can do.
                 - If we have no retailer policy: fall back to the
                   tracking chip alone. */}
            <div className="mt-3 flex flex-wrap gap-2" data-testid="purchase-windows">
              {claim.policyDays && claim.windowOpen && (
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] font-mono-pg uppercase tracking-widest px-2.5 py-1 border border-[#16A34A] bg-[#F0FDF4] text-[#16A34A]"
                  title={`${p.retailer_name}'s published price-adjustment window is ${claim.policyDays} days from purchase. Source: ${claim.source}.`}
                  data-testid="claim-window-chip"
                >
                  Claim window: {claim.daysLeftInWindow} day{claim.daysLeftInWindow === 1 ? "" : "s"} left ({p.retailer_name} {claim.policyDays}d)
                </span>
              )}
              {claim.policyDays && !claim.windowOpen && (
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] font-mono-pg uppercase tracking-widest px-2.5 py-1 border border-[#52525b] bg-[#f7f7f8] text-[#52525b]"
                  title={`${p.retailer_name}'s published price-adjustment window is ${claim.policyDays} days from purchase, so refund eligibility ended ${formatDateNZ(claim.claimEndsOn.toISOString().slice(0, 10))}. We're still watching the live price in the background in case the retailer honours a goodwill ask.`}
                  data-testid="claim-window-chip"
                >
                  Claim window closed ({p.retailer_name} {claim.policyDays}d)
                </span>
              )}
              {!claim.policyDays && daysLeft > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] font-mono-pg uppercase tracking-widest px-2.5 py-1 border border-[#e4e4e7] bg-white text-[#0a0a0a]"
                  data-testid="tracking-window-chip"
                >
                  Watching daily: {daysLeft} day{daysLeft === 1 ? "" : "s"} left
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            {p.product_url && (
              <a
                href={outboundRetailerUrl(p.product_url, p.retailer_id)}
                target="_blank"
                rel="sponsored nofollow noopener noreferrer"
                onClick={() => trackBuyNowClick({ retailerId: p.retailer_id, productUrl: p.product_url, context: "purchase_detail" })}
                className="bg-[#0a0a0a] text-white font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-[#002FA7] transition-colors"
                data-testid="view-retailer-listing-btn"
              >
                <ArrowSquareOut size={16} weight="bold" /> View live listing at {p.retailer_name}
              </a>
            )}
            <button onClick={refresh} disabled={refreshing} className="border border-black font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-black hover:text-white transition-colors disabled:opacity-60" data-testid="refresh-price-btn">
              <ArrowsClockwise size={16} className={refreshing ? "animate-spin" : ""} /> Refresh price
            </button>
            <button onClick={removeItem} className="border border-[#FF3B30] text-[#FF3B30] font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-[#FF3B30] hover:text-white transition-colors" data-testid="delete-purchase-btn">
              <Trash size={16} /> Stop tracking
            </button>
          </div>
        </div>

        {/* ACTIVE DROP HERO BANNER — when the live price has dropped below
            what the user paid, this is the most important thing on the
            page. Two variants:
              - Inside the retailer's claim window → bright green "draft
                the claim" CTA. High-confidence ask.
              - Outside the claim window → amber goodwill ask. Same draft,
                softer framing, so the user knows it's a long-shot but
                still possible (especially with PriceGuard's polite tone
                + recent receipt vault). */}
        {isDrop && claim.windowOpen && (
          <div className="border border-[#16A34A] bg-[#F0FDF4] p-6 mb-8" data-testid="active-drop-banner">
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <div className="flex-shrink-0 w-14 h-14 bg-[#16A34A] text-white flex items-center justify-center">
                <ArrowDown size={28} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="pg-overline text-[#16A34A]">Active price drop · claim ready to file</div>
                <div className="font-heading text-2xl font-black tracking-tight mt-1 text-[#0a0a0a]">
                  ${diff.toFixed(2)} back is yours for the asking.
                </div>
                <p className="text-sm text-[#52525b] mt-1.5 leading-relaxed">
                  The price has dropped by ${diff.toFixed(2)} and you're still inside {p.retailer_name}'s {claim.policyDays}-day price-adjustment window ({claim.daysLeftInWindow} day{claim.daysLeftInWindow === 1 ? "" : "s"} left). We've drafted the email - you just review, send.
                </p>
              </div>
              <a
                href="#claim-email-section"
                onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="claim-email-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                className="bg-[#16A34A] text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#138A3D] transition-colors flex-shrink-0"
                data-testid="active-drop-banner-cta"
              >
                Draft the claim <ArrowRight size={16} weight="bold" />
              </a>
            </div>
          </div>
        )}

        {isDrop && claim.policyDays && !claim.windowOpen && (
          <div className="border border-[#52525b] bg-[#f7f7f8] p-6 mb-8" data-testid="active-drop-banner-out-of-window">
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <div className="flex-shrink-0 w-14 h-14 bg-[#52525b] text-white flex items-center justify-center">
                <ArrowDown size={28} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="pg-overline text-[#52525b]">Price dropped, but claim window closed</div>
                <div className="font-heading text-2xl font-black tracking-tight mt-1 text-[#0a0a0a]">
                  ${diff.toFixed(2)} dropped, but {p.retailer_name}'s {claim.policyDays}-day window has closed.
                </div>
                <p className="text-sm text-[#52525b] mt-1.5 leading-relaxed">
                  We won't draft a formal claim from here once the published window has closed — that keeps PriceGuard requests credible with retailers. If you want to try a personal goodwill conversation directly with the store, that's still your call.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Price summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mb-10">
          <div className="bg-white p-6">
            <div className="pg-overline">You paid</div>
            <div className="font-heading text-3xl font-black mt-2 tracking-tighter">${p.purchase_price.toFixed(2)}</div>
          </div>
          <div className="bg-white p-6">
            <div className="pg-overline">Current price</div>
            <div className={`font-heading text-3xl font-black mt-2 tracking-tighter ${isDrop ? "text-[#D97706]" : ""}`}>${p.current_price.toFixed(2)}</div>
          </div>
          <div className="bg-white p-6">
            <div className="pg-overline">Price adjustment value</div>
            {isDrop ? (
              <div className="font-heading text-3xl font-black mt-2 tracking-tighter text-[#16A34A]">${diff.toFixed(2)}</div>
            ) : (
              <div className="font-heading text-3xl font-black mt-2 tracking-tighter text-[#52525b]">$0.00</div>
            )}
            {isDrop && <div className="pg-overline text-[#16A34A] mt-1">↓ {pct}%</div>}
          </div>
        </div>

        {/* PROMO CODE PANEL — surfaces PB Tech Pricebuster (or any retailer promo
            code our scraper detects) so the user knows there's an unclaimed
            discount available. We never auto-apply codes (retailers don't allow
            third parties to do that), but we put the code in their hands. */}
        {p.promo && p.promo.code && p.promo.promo_price && (
          <div className="border border-[#16A34A] bg-[#F0FDF4] p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="purchase-promo-panel">
            <div className="flex items-start gap-3 flex-1 min-w-[260px]">
              <div className="flex-shrink-0 w-10 h-10 bg-[#16A34A] text-white flex items-center justify-center font-heading font-black text-lg">%</div>
              <div className="flex-1 min-w-0">
                <div className="pg-overline text-[#16A34A]">Promo code available</div>
                <h3 className="font-heading font-bold tracking-tight mt-1 text-[#0a0a0a]">
                  Save ${Number(p.promo.savings || 0).toFixed(2)} at checkout with code <span className="font-mono-pg bg-[#0a0a0a] text-white px-2 py-0.5">{p.promo.code}</span>
                </h3>
                <p className="text-sm text-[#52525b] mt-1.5 leading-relaxed">
                  {p.retailer_name} is currently running {p.promo.promotion_name ? `the "${p.promo.promotion_name}"` : "a promotion"} that drops this product from <strong>${p.current_price.toFixed(2)}</strong> to <strong>${Number(p.promo.promo_price).toFixed(2)}</strong> when the code is entered at checkout. We can't apply it for you, but we'll keep it visible here so you don't miss it.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(p.promo.code);
                  toast.success(`Copied ${p.promo.code} - paste at ${p.retailer_name} checkout`);
                } catch {
                  toast.error("Couldn't copy. Long-press the code to copy manually.");
                }
              }}
              className="bg-[#0a0a0a] text-white font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-[#16A34A] transition-colors"
              data-testid="purchase-promo-copy-btn"
            >
              <Copy size={14} weight="bold" /> Copy code
            </button>
          </div>
        )}

        {/* BONUS PANEL — non-price extras attached to the product (free items,
            store credit, gift cards, cashback). Different signal from the
            promo code (price stays the same but the bundle value goes up). */}
        {p.bonus && p.bonus.description && (
          <div className="border border-[#D97706] bg-[#FFFBEB] p-5 mb-8 flex flex-wrap items-start gap-4" data-testid="purchase-bonus-panel">
            <div className="flex items-start gap-3 flex-1 min-w-[260px]">
              <div className="flex-shrink-0 w-10 h-10 bg-[#D97706] text-white flex items-center justify-center">
                <Gift size={20} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="pg-overline text-[#D97706]">Bonus included{p.bonus.value ? ` · worth $${Number(p.bonus.value).toFixed(2)}` : ""}</div>
                <h3 className="font-heading font-bold tracking-tight mt-1 text-[#0a0a0a]">
                  {p.bonus.description}
                </h3>
                <p className="text-sm text-[#52525b] mt-1.5 leading-relaxed">
                  {p.retailer_name} is bundling this extra in alongside the product. The listed price doesn't change, so it can be easy to miss. We'll keep flagging this here until the offer ends or you remove the tracking.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Chart */}
        <div className="border border-[#e4e4e7] bg-white p-6 mb-10" data-testid="price-history-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="pg-overline">Price history</div>
              <h2 className="font-heading text-2xl font-bold tracking-tight mt-1">Daily monitoring</h2>
            </div>
            {isDrop && (
              <span className="inline-flex items-center gap-1 bg-[#D97706] text-white text-xs font-bold px-3 py-1.5">
                <ArrowDown size={14} weight="bold" /> Drop detected
              </span>
            )}
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={p.price_history} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#f0f0f2" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#52525b", fontFamily: "IBM Plex Mono" }} />
                <YAxis tick={{ fontSize: 11, fill: "#52525b", fontFamily: "IBM Plex Mono" }} domain={["dataMin - 50", "dataMax + 50"]} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "none", color: "#fff", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <ReferenceLine y={p.purchase_price} stroke="#a1a1aa" strokeDasharray="3 3" label={{ value: "Paid", position: "left", fill: "#52525b", fontSize: 10 }} />
                <Line type="monotone" dataKey="price" stroke="#002FA7" strokeWidth={2.5} dot={{ r: 4, fill: "#002FA7" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Price match opportunities - only while still inside the protection window */}
        {daysLeft > 0 && (
          <div className="mb-10">
            <RetailerPolicyNote retailerId={p.retailer_id} daysLeft={daysLeft} />
            <div className="mt-6">
              <ComparePrices kind="purchase" id={p.purchase_id} source={p} onChange={load} />
            </div>
          </div>
        )}

        {/* Receipt attach / status */}
        <div id="purchase-receipt-section" className="border border-[#e4e4e7] bg-white p-6 mb-10" data-testid="purchase-receipt-section">
          <div className="flex flex-wrap items-start gap-5">
            {p.linked_vault_id ? (
              <>
                <CheckCircle size={32} weight="duotone" color="#16A34A" className="flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-[220px]">
                  <div className="pg-overline text-[#16A34A]">Receipt on file</div>
                  <h3 className="font-heading font-bold tracking-tight mt-1">This purchase has its receipt saved</h3>
                  <p className="text-[#52525b] text-sm mt-1 leading-relaxed">
                    The receipt for this item lives in your vault, ready if you ever need to show proof of purchase, claim a warranty, or add it to an insurance schedule.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Link to={`/dashboard/vault/${p.linked_vault_id}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#002FA7] hover:text-[#00207A]" data-testid="purchase-view-receipt-link">
                      <ReceiptIcon size={14} weight="bold" /> View the receipt
                    </Link>
                    <label className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0a0a0a] border border-black px-3 py-1.5 cursor-pointer hover:bg-black hover:text-white transition-colors" data-testid="purchase-replace-receipt-btn">
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        disabled={attaching}
                        onChange={(e) => attachReceipt(e.target.files?.[0])}
                        data-testid="purchase-replace-receipt-input"
                      />
                      <Upload size={12} weight="bold" /> {attaching ? "Replacing…" : "Replace"}
                    </label>
                    <button
                      type="button"
                      onClick={detachReceipt}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FF3B30] border border-[#FF3B30] px-3 py-1.5 hover:bg-[#FF3B30] hover:text-white transition-colors"
                      data-testid="purchase-detach-receipt-btn"
                    >
                      <Trash size={12} weight="bold" /> Detach
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <ReceiptIcon size={32} weight="duotone" color="#002FA7" className="flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-[220px]">
                  <div className="pg-overline text-[#002FA7]">Useful to have on file</div>
                  <h3 className="font-heading font-bold tracking-tight mt-1">Add the receipt for this purchase</h3>
                  <p className="text-[#52525b] text-sm mt-1 leading-relaxed">
                    Attach a photo or PDF of the receipt now and we'll keep it filed alongside this product. It's handy later for warranty claims, insurance, or just keeping a tidy record of what you own.
                  </p>
                  <label className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#002FA7] hover:bg-[#00207A] px-4 py-2 cursor-pointer" data-testid="purchase-attach-receipt-btn">
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      disabled={attaching}
                      onChange={(e) => attachReceipt(e.target.files?.[0])}
                      data-testid="purchase-attach-receipt-input"
                    />
                    <Upload size={14} weight="bold" /> {attaching ? "Attaching with AI…" : "Attach receipt"}
                  </label>
                  <p className="text-[10px] uppercase tracking-widest text-[#52525b] mt-2 font-mono-pg">
                    PDF, JPG, PNG, HEIC, WebP · max 10 MB
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Price adjustment claim */}
        <div id="claim-email-section" className="border border-[#e4e4e7] bg-white p-6" data-testid="claim-email-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <div className="pg-overline">Price adjustment claim assistant</div>
              <h2 className="font-heading text-2xl font-bold tracking-tight mt-1">File a price adjustment claim</h2>
              {(() => {
                // Strict eligibility gate (iter26). Show one of three
                // outcomes: ready-to-draft, blocked-no-drop, or
                // blocked-window-closed. Each carries its own message
                // so the user knows exactly why the button is locked.
                const eligible = isDrop && claim.windowOpen;
                if (eligible) {
                  return (
                    <p className="text-[#52525b] text-sm mt-3 leading-relaxed" data-testid="claim-eligible-copy">
                      Draft a polite, retailer-ready note asking for the ${diff.toFixed(2)} price adjustment refund. Edit, copy, send.
                    </p>
                  );
                }
                if (!isDrop) {
                  return (
                    <div className="text-sm mt-3 leading-relaxed bg-[#F0FDF4] border border-[#16A34A]/30 p-4" data-testid="claim-blocked-no-drop">
                      <div className="pg-overline text-[#16A34A] mb-1">No claim available yet</div>
                      <p className="text-[#0a0a0a]">
                        The current price isn't lower than what you paid. We'll keep watching daily — once the price drops below ${p.purchase_price.toFixed(2)}, this section unlocks automatically.
                      </p>
                    </div>
                  );
                }
                // Drop exists but window closed (or no policy at all).
                return (
                  <div className="text-sm mt-3 leading-relaxed bg-[#FEF2F2] border border-[#FF3B30]/30 p-4" data-testid="claim-blocked-window-closed">
                    <div className="pg-overline text-[#FF3B30] mb-1">Claim window closed</div>
                    <p className="text-[#0a0a0a]">
                      The price has dropped by ${diff.toFixed(2)}, but {p.retailer_name}'s {claim.policyDays ? `${claim.policyDays}-day ` : ""}price-adjustment window has closed for this purchase. We won't draft a formal claim from here to avoid spamming the retailer, but you can still reach out to the store directly for a goodwill conversation.
                    </p>
                  </div>
                );
              })()}
              <label className="block mt-5">
                <span className="pg-overline">Add context (optional)</span>
                <textarea value={notes} onChange={(e)=>setNotes(e.target.value)} disabled={!isDrop || !claim.windowOpen} className="mt-2 w-full border border-[#e4e4e7] p-3 text-sm focus:outline-none focus:border-[#002FA7] disabled:bg-[#f7f7f8] disabled:cursor-not-allowed" rows={3} placeholder="e.g. I'm a regular customer and have my order number ready." data-testid="claim-notes" />
              </label>
              <button onClick={generateClaim} disabled={!isDrop || !claim.windowOpen || genBusy} className="mt-4 bg-[#002FA7] text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#00207A] disabled:opacity-50 disabled:cursor-not-allowed w-full justify-center" data-testid="generate-claim-btn">
                <EnvelopeSimple size={18} weight="bold" /> {genBusy ? "Drafting with AI…" : "Draft the claim"}
              </button>
            </div>
            <div className="lg:col-span-7">
              {email ? (
                <div className="bg-[#f7f7f8] border border-[#e4e4e7] p-5" data-testid="generated-email">
                  <div className="flex items-center justify-between mb-3">
                    <div className="pg-overline">Subject</div>
                    <button onClick={() => copy(email.subject)} className="text-xs font-bold inline-flex items-center gap-1 hover:text-[#002FA7]" data-testid="copy-subject">
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                  <div className="font-mono-pg text-sm text-black bg-white border border-[#e4e4e7] p-3" data-testid="email-subject">{email.subject}</div>

                  <div className="flex items-center justify-between mt-5 mb-3">
                    <div className="pg-overline">Body</div>
                    <button onClick={() => copy(email.body)} className="text-xs font-bold inline-flex items-center gap-1 hover:text-[#002FA7]" data-testid="copy-body">
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                  <pre className="font-mono-pg text-sm text-black bg-white border border-[#e4e4e7] p-4 whitespace-pre-wrap leading-relaxed" data-testid="email-body">{email.body}</pre>

                  {/* AUTO-CLAIM SEND PANEL */}
                  <div className="mt-6 border-t border-[#e4e4e7] pt-5" data-testid="auto-claim-panel">
                    {claimSent ? (
                      <div className="border border-[#16A34A] bg-[#F0FDF4] p-4" data-testid="claim-sent-banner">
                        <div className="flex items-start gap-3">
                          <CheckCircle size={22} weight="fill" color="#16A34A" className="flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-heading font-bold text-[#0a0a0a]">
                              {claimSent.self_submitted ? `Submission recorded · ${claimSent.sent_to}` : `Sent to ${claimSent.sent_to}`}
                            </div>
                            <div className="text-sm text-[#52525b] mt-1">
                              {claimSent.self_submitted
                                ? `We've logged that you submitted this claim to ${p.retailer_name} via their form. We won't email anything from PriceGuard. Keep an eye on ${claimSent.reply_to} for their reply.`
                                : <>Any reply from {p.retailer_name} will land directly in your inbox ({claimSent.reply_to}). We've kept a record of this submission in your account.</>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (() => {
                      // Form-mode retailers (JB Hi-Fi NZ) don't accept
                      // email submissions, so we replace the "Send via
                      // PriceGuard" panel with a copy-and-open-form flow.
                      const policy = RETAILER_POLICIES[p.retailer_id] || {};
                      if (policy.submitMode === "form") {
                        return (
                          <div data-testid="claim-form-submit-controls">
                            <div className="pg-overline mb-2">How to submit at {p.retailer_name}</div>
                            <p className="text-sm text-[#52525b] leading-relaxed mb-3">
                              {p.retailer_name} only accepts price-adjustment requests via their online contact form, not by email. Copy the draft above, then open the form and paste it in. They'll reply directly to your inbox.
                            </p>
                            <ol className="text-sm text-[#0a0a0a] space-y-1.5 mb-4 ml-4 list-decimal">
                              <li>Copy the subject and body (buttons above).</li>
                              <li>Open the {p.retailer_name} contact form in a new tab.</li>
                              <li>Paste the body into the message field, set the subject if asked.</li>
                            </ol>
                            <div className="flex flex-wrap gap-3">
                              <button
                                type="button"
                                onClick={async () => { await copy(`${email.subject}\n\n${email.body}`); toast.success("Subject + body copied"); }}
                                className="bg-[#0a0a0a] text-white font-bold px-5 py-3 inline-flex items-center gap-2 hover:bg-[#1a1a1a]"
                                data-testid="claim-form-copy-btn"
                              >
                                <Copy size={16} weight="bold" /> Copy draft
                              </button>
                              <a
                                href={policy.submitUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#002FA7] text-white font-bold px-5 py-3 inline-flex items-center gap-2 hover:bg-[#00207A]"
                                data-testid="claim-form-open-btn"
                              >
                                <ArrowSquareOut size={16} weight="bold" /> Open {p.retailer_name} form
                              </a>
                            </div>
                            <div className="mt-5 border-t border-[#e4e4e7] pt-4">
                              <p className="text-xs text-[#52525b] leading-relaxed mb-2">
                                <strong>Already pasted + submitted via the form?</strong> Tap below so we can keep a record of this claim alongside the email-sent ones. No follow-up email goes out from PriceGuard.
                              </p>
                              <button
                                type="button"
                                onClick={markSelfSubmitted}
                                disabled={sendingClaim}
                                className="bg-[#16A34A] text-white font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-[#15803D] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                data-testid="claim-form-mark-submitted-btn"
                              >
                                <CheckCircle size={16} weight="fill" /> {sendingClaim ? "Recording…" : "Mark as submitted"}
                              </button>
                            </div>
                            <p className="text-[10px] font-mono-pg text-[#52525b] mt-3 uppercase tracking-widest">
                              Paste · submit · keep an eye on your inbox
                            </p>
                          </div>
                        );
                      }
                      // Default email-mode path: gate on receipt, then
                      // offer the auto-send button.
                      if (!p.linked_vault_id) {
                        return (
                          <div className="border border-[#D97706] bg-[#FFFBEB] p-4" data-testid="claim-receipt-required">
                            <div className="flex items-start gap-3">
                              <Warning size={22} weight="fill" color="#D97706" className="flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <div className="font-heading font-bold text-[#0a0a0a]">Add the receipt before sending</div>
                                <div className="text-sm text-[#52525b] mt-1 leading-relaxed">
                                  {p.retailer_name} will want to see proof of purchase. Attach your receipt and we'll automatically include it as an attachment when we send the claim on your behalf.
                                </div>
                                <a href="#purchase-receipt-section" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid=purchase-receipt-section]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#002FA7] hover:text-[#00207A] underline" data-testid="claim-jump-to-receipt">
                                  Attach a receipt now ↑
                                </a>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div data-testid="claim-send-controls">
                          <div className="pg-overline mb-2">Send on your behalf</div>
                          <p className="text-sm text-[#52525b] leading-relaxed mb-3">
                            We'll send this email to <strong>{p.retailer_name}</strong>'s customer service team with your receipt attached. The retailer sees it as "via PriceGuard" and any reply goes straight to your inbox. You stay in control - the retailer's decision is always final.
                          </p>
                          <label className="block mb-3">
                            <span className="pg-overline">Order or invoice number (optional)</span>
                            <input
                              type="text"
                              value={orderNumber}
                              onChange={(e) => setOrderNumber(e.target.value)}
                              placeholder="e.g. WEB-12345678"
                              className="mt-2 w-full border border-[#e4e4e7] p-3 text-sm font-mono-pg focus:outline-none focus:border-[#002FA7]"
                              data-testid="claim-order-number-input"
                            />
                            <span className="block text-xs text-[#52525b] mt-1">Helps the retailer locate your purchase faster. Find this on your receipt or order email.</span>
                          </label>
                          <button
                            type="button"
                            onClick={sendClaimToRetailer}
                            disabled={sendingClaim}
                            className="bg-[#16A34A] text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#15803D] disabled:opacity-50 disabled:cursor-not-allowed w-full justify-center"
                            data-testid="send-claim-btn"
                          >
                            <PaperPlaneTilt size={18} weight="fill" /> {sendingClaim ? "Sending to retailer…" : `Send to ${p.retailer_name} on my behalf`}
                          </button>
                          <p className="text-[10px] font-mono-pg text-[#52525b] mt-3 uppercase tracking-widest text-center">
                            Receipt is attached automatically · reply lands in your inbox · refund is the retailer's decision
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-[#a1a1aa] bg-[#f7f7f8] p-8 h-full flex items-center justify-center text-center min-h-[280px]">
                  <div>
                    <EnvelopeSimple size={36} weight="duotone" color="#002FA7" className="mx-auto" />
                    <p className="font-heading font-bold tracking-tight mt-3">Your draft will appear here</p>
                    <p className="text-[#52525b] text-sm mt-1">Generated by AI · ready to copy</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Warranty claim assistant (CGA 1993 anchored) */}
        <div className="border border-[#e4e4e7] bg-white p-6 mt-10" data-testid="warranty-claim-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <div className="pg-overline">Warranty claim assistant</div>
              <h2 className="font-heading text-2xl font-bold tracking-tight mt-1">Something broken? Draft a warranty claim.</h2>
              <p className="text-[#52525b] text-sm mt-3 leading-relaxed">
                When a product fails earlier than it reasonably should, the New Zealand Consumer Guarantees Act 1993 covers you, even after the manufacturer's warranty has ended. We'll draft a polite, factual claim email grounded in the CGA's acceptable-quality and reasonable-durability guarantees.
              </p>
              <label className="block mt-5">
                <span className="pg-overline">What's wrong with the product?</span>
                <textarea
                  value={warrantyIssue}
                  onChange={(e) => setWarrantyIssue(e.target.value)}
                  className="mt-2 w-full border border-[#e4e4e7] p-3 text-sm focus:outline-none focus:border-[#002FA7]"
                  rows={4}
                  placeholder="e.g. The screen has developed a dead column of pixels after 14 months of normal use."
                  data-testid="warranty-issue-input"
                />
              </label>
              <label className="block mt-4">
                <span className="pg-overline">What have you tried? (optional)</span>
                <textarea
                  value={warrantyTried}
                  onChange={(e) => setWarrantyTried(e.target.value)}
                  className="mt-2 w-full border border-[#e4e4e7] p-3 text-sm focus:outline-none focus:border-[#002FA7]"
                  rows={2}
                  placeholder="e.g. Hard reset, factory reset, swapping cables."
                  data-testid="warranty-tried-input"
                />
              </label>
              <label className="block mt-4">
                <span className="pg-overline">Manufacturer warranty length (months, optional override)</span>
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={warrantyMonthsOverride}
                  onChange={(e) => setWarrantyMonthsOverride(e.target.value)}
                  className="mt-2 w-32 border border-[#e4e4e7] p-3 text-sm focus:outline-none focus:border-[#002FA7] font-mono-pg"
                  placeholder="auto"
                  data-testid="warranty-months-override-input"
                />
                <span className="block text-xs text-[#52525b] mt-1">Leave blank to use the typical warranty for this category.</span>
              </label>
              <button
                onClick={generateWarrantyClaim}
                disabled={warrantyBusy || warrantyIssue.trim().length < 10}
                className="mt-5 bg-[#002FA7] text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#00207A] disabled:opacity-50 disabled:cursor-not-allowed w-full justify-center"
                data-testid="generate-warranty-claim-btn"
              >
                <Wrench size={18} weight="bold" /> {warrantyBusy ? "Drafting with AI…" : "Draft warranty claim"}
              </button>
              <p className="text-[10px] font-mono-pg text-[#52525b] mt-3 uppercase tracking-widest">
                Draft only · the remedy is the retailer's call · refunds never guaranteed
              </p>
            </div>
            <div className="lg:col-span-7">
              {warrantyEmail ? (
                <div className="bg-[#f7f7f8] border border-[#e4e4e7] p-5" data-testid="warranty-email-result">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="pg-overline">Warranty status</div>
                      <div className="font-mono-pg text-xs mt-1">
                        {warrantyEmail.inside_manufacturer_window
                          ? `Inside typical manufacturer window (${warrantyEmail.warranty_days_left} days remaining)`
                          : `Outside typical manufacturer window (${Math.abs(warrantyEmail.warranty_days_left)} days past) · relying on CGA reasonable durability`}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={saveWarrantyDraft} className="text-xs font-bold inline-flex items-center gap-1 border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors" data-testid="save-warranty-draft-btn">
                        Save draft
                      </button>
                      <button onClick={clearWarrantyDraft} className="text-xs font-bold inline-flex items-center gap-1 border border-[#FF3B30] text-[#FF3B30] px-3 py-1.5 hover:bg-[#FF3B30] hover:text-white transition-colors" data-testid="clear-warranty-draft-btn">
                        Discard
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-3 mt-5">
                    <div className="pg-overline">Subject</div>
                    <button onClick={() => copy(warrantyEmail.subject)} className="text-xs font-bold inline-flex items-center gap-1 hover:text-[#002FA7]" data-testid="copy-warranty-subject">
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                  <div className="font-mono-pg text-sm text-black bg-white border border-[#e4e4e7] p-3" data-testid="warranty-email-subject">{warrantyEmail.subject}</div>

                  <div className="flex items-center justify-between mt-5 mb-3">
                    <div className="pg-overline">Body</div>
                    <button onClick={() => copy(warrantyEmail.body)} className="text-xs font-bold inline-flex items-center gap-1 hover:text-[#002FA7]" data-testid="copy-warranty-body">
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                  <pre className="font-mono-pg text-sm text-black bg-white border border-[#e4e4e7] p-4 whitespace-pre-wrap leading-relaxed" data-testid="warranty-email-body">{warrantyEmail.body}</pre>
                </div>
              ) : (
                <div className="border border-dashed border-[#a1a1aa] bg-[#f7f7f8] p-8 h-full flex items-center justify-center text-center min-h-[280px]">
                  <div>
                    <Wrench size={36} weight="duotone" color="#002FA7" className="mx-auto" />
                    <p className="font-heading font-bold tracking-tight mt-3">Your warranty draft will appear here</p>
                    <p className="text-[#52525b] text-sm mt-1">CGA 1993 grounded · plain English · polite</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
